import { Component, OnInit, OnDestroy, ViewChild, HostListener, ChangeDetectorRef, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { IonicModule } from '@ionic/angular';
import { LeftMenuService } from '@services/left-menu.service';
import { AuthServiceService } from '@services/auth-service.service';
import { Observable, of, Subscription, interval } from 'rxjs';
import { IonContent, IonMenu, MenuController, ModalController } from '@ionic/angular';
import { RegUser } from 'src/app/pages/basic-reg/RegUser';
import { Globals } from 'src/app/globals';
import { Router, NavigationEnd } from '@angular/router';

import { CompleteRegConfirmationComponent } from '@modal/complete-reg-confirmation/complete-reg-confirmation.component';

import { Inject } from '@angular/core';
@Component({
  selector: 'app-header',
  standalone: true,
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  providers: [LeftMenuService, AuthServiceService],
  imports: [CommonModule, RouterModule, IonicModule]
})
export class HeaderComponent implements OnInit, OnDestroy {


  user!: RegUser;
  LoggedIn: boolean = false;
  isHome: boolean = false;
  isMobile: boolean = false;
  isScrolled: boolean = false;
  private authCheckSubscription?: Subscription;
  private routerSubscription?: Subscription;
  private removeScrollListeners: Array<() => void> = [];
  private scrollRafId: number | null = null;

  getMobileStatus(): boolean {
    // Use matchMedia for robust detection
    if (window.matchMedia) {
      return window.matchMedia('(max-width: 991px)').matches;
    }
    // Fallback to innerWidth
    return window.innerWidth <= 991;
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.isMobile = this.getMobileStatus();
  }

  @ViewChild(IonContent) content!: IonContent;
  @ViewChild(IonMenu) menuEl!: IonMenu;
  @ViewChild('target') target: any;

  constructor(
    @Inject(LeftMenuService) private leftMenuSvc: LeftMenuService,
    @Inject(AuthServiceService) private authSvc: AuthServiceService,
    @Inject(MenuController) private menuCtrl: MenuController,
    @Inject(ModalController) public modalCtrl: ModalController,
    @Inject(Globals) private globals: Globals,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone
  ) { }

  ngOnInit() {
    // Initial load
    this.updateAuthState();
    this.isMobile = this.getMobileStatus();
    this.isScrolled = window.scrollY > 50;
    
    // Check auth state periodically to detect login/logout changes (every 500ms)
    this.authCheckSubscription = interval(500).subscribe(() => {
      this.updateAuthState();
    });

    // Also check on any route changes
    this.routerSubscription = this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        // Small delay to ensure data is loaded
        setTimeout(() => {
          this.updateAuthState();
          this.initHeaderScrollState();
        }, 150);
      }
    });

    this.initHeaderScrollState();
  }

  ngOnDestroy() {
    // Clean up subscriptions
    if (this.authCheckSubscription) {
      this.authCheckSubscription.unsubscribe();
    }
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }

    this.clearScrollListeners();
  }

  private initHeaderScrollState() {
    this.clearScrollListeners();

    this.ngZone.runOutsideAngular(() => {
      const onAnyScroll = () => {
        if (this.scrollRafId !== null) {
          return;
        }

        this.scrollRafId = window.requestAnimationFrame(() => {
          this.scrollRafId = null;

          const nextScrolled = this.getCurrentScrollTop() > 50;
          if (nextScrolled !== this.isScrolled) {
            this.ngZone.run(() => {
              this.isScrolled = nextScrolled;
            });
          }
        });
      };

      window.addEventListener('scroll', onAnyScroll, { passive: true });
      this.removeScrollListeners.push(() => window.removeEventListener('scroll', onAnyScroll));

      document.addEventListener('scroll', onAnyScroll, { passive: true, capture: true });
      this.removeScrollListeners.push(() => document.removeEventListener('scroll', onAnyScroll, true));

      const ionContents = Array.from(document.querySelectorAll('ion-content')) as any[];
      ionContents.forEach((content) => {
        if (typeof content.getScrollElement === 'function') {
          content.getScrollElement().then((scrollEl: HTMLElement) => {
            scrollEl.addEventListener('scroll', onAnyScroll, { passive: true });
            this.removeScrollListeners.push(() => scrollEl.removeEventListener('scroll', onAnyScroll));
          }).catch(() => {
            // no-op
          });
        }
      });

      onAnyScroll();
    });
  }


  private getCurrentScrollTop(): number {
    let maxScrollTop = window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;

    const ionContents = Array.from(document.querySelectorAll('ion-content')) as any[];
    ionContents.forEach((content) => {
      if (typeof content.scrollTop === 'number') {
        maxScrollTop = Math.max(maxScrollTop, content.scrollTop);
      }

      const shadowScroll = content?.shadowRoot?.querySelector?.('.inner-scroll') as HTMLElement | null;
      if (shadowScroll) {
        maxScrollTop = Math.max(maxScrollTop, shadowScroll.scrollTop || 0);
      }
    });

    return maxScrollTop;
  }

  private clearScrollListeners() {
    this.removeScrollListeners.forEach((remove) => remove());
    this.removeScrollListeners = [];

    if (this.scrollRafId !== null) {
      window.cancelAnimationFrame(this.scrollRafId);
      this.scrollRafId = null;
    }
  }
  updateAuthState() {
    const wasLoggedIn = this.LoggedIn;
    this.LoggedIn = this.authSvc.isLoggedIn();
    
    if (this.LoggedIn) {
      // User is logged in, fetch user data
      const currentUser = this.authSvc.getLoggedInUser();
      if (currentUser && currentUser.firstName) {
        this.user = currentUser;
        // console.log('Header: User loaded -', this.user.firstName);
        // Force change detection to update the view
        this.cdr.detectChanges();
      } else {
        // console.log('Header: User logged in but data not available yet');
      }
    } else {
      // User is logged out, clear user data
      if (this.user) {
        // console.log('Header: Clearing user data');
      }
      this.user = null!;
      this.cdr.detectChanges();
    }
  }

  ionViewDidEnter() {
    this.updateAuthState();
    this.isMobile = this.getMobileStatus();
  }

  toggleMenu() {
    this.leftMenuSvc.toggleMenu();
  }

  scrollTo(id: string) {
    const target = document.querySelector('#' + id) as HTMLDivElement;

    this.content.scrollToPoint(0, target.offsetTop + (target.offsetHeight / 1.5), 1000)
    this.menuCtrl.close();
  }

  logout() {
    this.authSvc.logout();
    this.updateAuthState(); // Update immediately after logout
    this.router.navigate(['/home']);
  }

  async openCompleteRegConfirmation() {
    const modal = await this.modalCtrl.create({
      component: CompleteRegConfirmationComponent,
      componentProps: {
      }
    });

    return await modal.present();
  }

  showStarIcon(user: RegUser | undefined | null): boolean {
    if (!user) return false;
    const isRole3WithParentAndSeqQue = user.roleLevel === 3 && user.parentId != undefined && user.parentId != null && user.parentId != 0 && user.isSeqQueUploaded;
    const isRole4or5 = user.roleLevel === 4 || user.roleLevel === 5;
    return (isRole3WithParentAndSeqQue || isRole4or5) || !!user.isAccDetailVerfied;
  }

  showRegisterButton(user: RegUser | undefined | null): boolean {
    if (user == undefined || user == null) return true;
    const isRole3WithParentAndNotSeqQue = user.roleLevel === 3 && user.parentId != undefined && user.parentId != null && user.parentId != 0 && !user.isSeqQueUploaded;
    const isRole0to2AndNotDocument = (user.roleLevel === 0 || user.roleLevel === 1 || user.roleLevel === 2) && !user.isAccDetailVerfied;
    return isRole3WithParentAndNotSeqQue || isRole0to2AndNotDocument;
  }
  // async verificationMail() {
  //   const modal = await this.modalController.create({
  //     component: EmailVerificationInfoComponent,
  //     componentProps: {
  //       errorMsg: 'Only jpg/jpeg and png files are allowed!',
  //       imageSrc: 'assets/images/email-icon.png'
  //     }
  //   });
  //   return await modal.present();
  // }

  async navigateAndCloseMenu(route: string | any[]) {
    if (this.menuEl) {
      await this.menuEl.close();
    } else {
      await this.menuCtrl.close('main-menu');
    }
    setTimeout(() => {
      this.router.navigate(Array.isArray(route) ? route : [route]);
    }, 250);
  }

}
